import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { TokenManager } from './api';
import emailjs from '@emailjs/browser';


const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openSignUp, openSignIn, signOut } = useClerk();
  const { isSignedIn, user, isLoaded } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStartStep, setModalStartStep] = useState(1);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Detect onboarding query param and open modal at correct step
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const onboardingStep = params.get('onboarding');
    if (onboardingStep) {
      setModalStartStep(Number(onboardingStep));
      setIsModalOpen(true);
      // Optionally, clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search]);

  // Check if user needs to complete onboarding after signing up
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      // Check if this user has completed onboarding
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
      
      if (!hasCompletedOnboarding) {
        // User is signed in but hasn't completed onboarding - open modal at step 2
        setModalStartStep(2);
        setIsModalOpen(true);
      }
    }
  }, [isLoaded, isSignedIn, user]);

  // Handle scroll-based header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past initial 100px - hide header
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    openSignIn();
  };

  const handleLogout = async () => {
    try {
      // Clear any legacy tokens and user data from localStorage
      TokenManager.clearTokens();
      localStorage.removeItem('userPhone');
      
      // Sign out from Clerk
      await signOut();
      
      // Refresh the page to ensure clean state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Even if Clerk signOut fails, clear local tokens
      TokenManager.clearTokens();
      localStorage.removeItem('userPhone');
      
      // Refresh the page
      window.location.reload();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalStartStep(1);
  };

  const handleContactUs = () => {
    setIsContactFormOpen(true);
  };

  const handleCloseContactForm = () => {
    setIsContactFormOpen(false);
    setContactForm({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setFormErrors([]);
    setIsSubmitting(false);
    setSubmitStatus('idle');
    setSubmitMessage('');
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Map form field names to state properties
    let stateKey = name;
    if (name === 'from_name') stateKey = 'name';
    if (name === 'from_email') stateKey = 'email';
    if (name === 'user_subject') stateKey = 'subject';
    
    setContactForm({
      ...contactForm,
      [stateKey]: value
    });
    
    // Clear errors when user starts typing
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    
    if (!contactForm.name.trim()) errors.push('Name is required');
    if (!contactForm.email.trim()) errors.push('Email is required');
    if (!contactForm.subject.trim()) errors.push('Subject is required');
    if (!contactForm.message.trim()) errors.push('Message is required');
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (contactForm.email && !emailRegex.test(contactForm.email)) {
      errors.push('Please enter a valid email address');
    }
    
    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleContactFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');
    
    // EmailJS configuration
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    
    // Check if EmailJS is properly configured
    if (!serviceId || !templateId || !publicKey || 
        serviceId === 'your_service_id' || 
        templateId === 'your_template_id_here' || 
        publicKey === 'your_public_key') {
      
      setSubmitStatus('error');
      setSubmitMessage('Email service not configured. Please contact us directly at wajahat.test@gmail.com');
      setIsSubmitting(false);
      return;
    }
    
    if (formRef.current) {
      emailjs
        .sendForm(serviceId, templateId, formRef.current, publicKey)
        .then(
          (result) => {
            console.log('✅ Email sent successfully:', result.text);
            setSubmitStatus('success');
            setSubmitMessage('Your message has been sent successfully! We\'ll get back to you soon.');
            
            // Clear form after successful submission
            setTimeout(() => {
              handleCloseContactForm();
            }, 3000);
          },
          (error) => {
            console.error('❌ EmailJS Error:', error);
            setSubmitStatus('error');
            
            // More specific error handling
            if (error && error.text) {
              if (error.text.includes('Gmail_API') || error.text.includes('insufficient authentication') || error.text.includes('Request had insufficient authentication scopes')) {
                setSubmitMessage('⚠️ Gmail authentication issue: Your EmailJS Gmail service needs re-authorization. Please contact us directly at wajahat.test@gmail.com while we fix this.');
              } else if (error.text.includes('rate limited') || error.text.includes('quota')) {
                setSubmitMessage('Too many requests. Please wait a moment and try again, or contact us at wajahat.test@gmail.com');
              } else if (error.text.includes('network') || error.text.includes('fetch')) {
                setSubmitMessage('Network error. Please check your connection and try again, or contact us at wajahat.test@gmail.com');
              } else {
                setSubmitMessage(`Error: ${error.text}. Please contact us directly at wajahat.test@gmail.com`);
              }
            } else {
              setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly at wajahat.test@gmail.com');
            }
          }
        )
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur-sm transition-all duration-300 transform ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Logo and Desktop Navigation */}
            <div className="flex items-center space-x-4 sm:space-x-6">
              <img 
                src="/images/noBgColor.png" 
                alt="Silas Logo" 
                className="h-6 sm:h-8 w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
              
              {/* Desktop Navigation Menu */}
              <nav className="hidden md:flex items-center space-x-6">
                {/* Why Silas Button */}
                <button 
                  onClick={() => {
                    const breakingSilenceSection = document.getElementById('breaking-silence');
                    if (breakingSilenceSection) {
                      breakingSilenceSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-black transition-all duration-300 font-medium relative overflow-hidden group"
                >
                  <span className="relative z-10">Why Silas?</span>
                  <div className="absolute inset-0 bg-gray-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center rounded-lg"></div>
                </button>
                
                {/* Pricing Button */}
                <button 
                  onClick={() => {
                    const pricingSection = document.getElementById('pricing-section');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-black transition-all duration-300 font-medium relative overflow-hidden group"
                >
                  <span className="relative z-10">Pricing</span>
                  <div className="absolute inset-0 bg-gray-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center rounded-lg"></div>
                </button>
              </nav>
            </div>
            
            {/* Right side - Desktop Auth buttons */}
            <nav className="hidden md:flex items-center space-x-4">
              {!isLoaded ? null : isSignedIn ? (
                <>
                  <button 
                    onClick={() => navigate('/home')}
                    className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-300 font-medium transform hover:scale-105 hover:shadow-md"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-gray-600 hover:text-black border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-300 font-medium transform hover:scale-105"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={handleGetStarted}
                    className="px-4 py-2 text-gray-600 hover:text-black transition-all duration-300 font-medium relative overflow-hidden group"
                  >
                    <span className="relative z-10">Sign In</span>
                    <div className="absolute inset-0 bg-gray-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg"></div>
                  </button>
                  
                  <button 
                    onClick={handleSignUp}
                    className="px-6 py-2 rounded-lg transition-all duration-300 bg-black text-white hover:bg-gray-800 font-medium transform hover:scale-105 hover:shadow-md"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-black hover:bg-gray-50 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-100">
              <nav className="flex flex-col space-y-3 pt-4">
                {/* Why Silas Button */}
                <button 
                  onClick={() => {
                    const breakingSilenceSection = document.getElementById('breaking-silence');
                    if (breakingSilenceSection) {
                      breakingSilenceSection.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-3 text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all duration-200 font-medium"
                >
                  Why Silas?
                </button>
                
                {/* Pricing Button */}
                <button 
                  onClick={() => {
                    const pricingSection = document.getElementById('pricing-section');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left px-4 py-3 text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all duration-200 font-medium"
                >
                  Pricing
                </button>

                {/* Mobile Auth buttons */}
                <div className="pt-3 border-t border-gray-100 space-y-3">
                  {!isLoaded ? null : isSignedIn ? (
                    <>
                      <button 
                        onClick={() => {
                          navigate('/home');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
                      >
                        Dashboard
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-gray-600 hover:text-black border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={() => {
                          handleGetStarted();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-gray-600 hover:text-black hover:bg-gray-50 rounded-lg transition-all duration-200 font-medium"
                      >
                        Sign In
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleSignUp();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
                      >
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden pt-16 sm:pt-20">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 border border-gray-200 text-gray-700 text-sm mb-8 font-medium animate-fade-in-up opacity-0 animation-delay-300">
              Trusted by 50,000+ men
            </div> */}
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-black leading-tight mb-4 sm:mb-6 animate-fade-in-up opacity-0 animation-delay-500 px-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Your mental health matters.
              <span className="text-gray-600 block mt-1 sm:mt-2 animate-fade-in-up opacity-0 animation-delay-700">
                Let's talk about it.
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-10 leading-relaxed max-w-3xl mx-auto animate-fade-in-up opacity-0 animation-delay-900 px-4" style={{ fontWeight: 400 }}>
              Break free from silence. Connect with empathetic support designed specifically for men. 
              No judgment, no waiting lists, no barriers—just genuine understanding when you need it most.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 animate-fade-in-up opacity-0 animation-delay-1100 px-4">
              <button 
                onClick={isSignedIn ? () => navigate('/home') : handleSignUp}
                className="px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all duration-300 shadow-sm hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white group"
              >
                {isSignedIn ? (
                  <>
                    Go to Dashboard
                    <span className="text-lg sm:text-xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </>
                ) : (
                  <>
                    Start Talking Today
                    <span className="text-lg sm:text-xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </>
                )}
              </button>
              {/* <button className="bg-white border border-gray-200 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2 group transform hover:scale-105">
                <span className="text-gray-500 group-hover:scale-110 transition-transform duration-300">▶</span>
                Watch How It Works
              </button> */}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 lg:gap-8 max-w-3xl mx-auto animate-fade-in-up opacity-0 animation-delay-1300 px-4">
              <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg transition-all duration-300 hover:shadow-md hover:scale-105 group">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>24/7</div>
                <div className="text-gray-600 text-xs sm:text-sm font-medium">Always Available</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg transition-all duration-300 hover:shadow-md hover:scale-105 group">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>100%</div>
                <div className="text-gray-600 text-xs sm:text-sm font-medium">Confidential</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg transition-all duration-300 hover:shadow-md hover:scale-105 group">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>0</div>
                <div className="text-gray-600 text-xs sm:text-sm font-medium">Judgment</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg transition-all duration-300 hover:shadow-md hover:scale-105 group">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>2min</div>
                <div className="text-gray-600 text-xs sm:text-sm font-medium">To Get Started</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-3 sm:mb-4 animate-fade-in-up opacity-0 animation-delay-300 px-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>How Silas Works</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up opacity-0 animation-delay-500 px-4">
              Getting support shouldn't be complicated. Here's how we make it simple.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
            <div className="text-center group animate-fade-in-up opacity-0 animation-delay-600 h-full">
              <div className="relative mb-6 h-full">
                <div className="relative bg-white border border-gray-200 rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm hover:shadow-lg transition-all duration-500 transform group-hover:scale-105 group-hover:-translate-y-2 h-full flex flex-col">
                  <div className="text-black font-bold text-lg mb-4 relative">
                    <span className="relative z-10 text-2xl" style={{ fontWeight: 700 }}>01</span>
                    <div className="absolute inset-0 bg-black rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 opacity-10"></div>
                  </div>
                  {/* <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-200 transition-colors duration-300">
                    <div className="w-6 h-6 bg-black rounded-sm"></div>
                  </div> */}
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-black mb-3 sm:mb-4" style={{ fontWeight: 600 }}>Start a conversation</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                  Just send a text. No forms. No appointments. There’s nothing to download or schedule. Simply start texting Silas—share whatever’s on your mind.We’re here to listen, support, and guide you—one message at a time.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-auto">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">Instant access</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">No signup required</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center group animate-fade-in-up opacity-0 animation-delay-800 h-full">
              <div className="relative mb-6 h-full">
                <div className="relative bg-white border border-gray-200 rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm hover:shadow-lg transition-all duration-500 transform group-hover:scale-105 group-hover:-translate-y-2 h-full flex flex-col">
                  <div className="text-black font-bold text-lg mb-4 relative">
                    <span className="relative z-10 text-2xl" style={{ fontWeight: 700 }}>02</span>
                    <div className="absolute inset-0 bg-black rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 opacity-10"></div>
                  </div>
                  {/* <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-200 transition-colors duration-300">
                    <div className="w-8 h-8 border-2 border-black rounded-full relative">
                      <div className="w-2 h-2 bg-black rounded-full absolute top-1 left-1"></div>
                      <div className="w-2 h-2 bg-black rounded-full absolute top-1 right-1"></div>
                    </div>
                  </div> */}
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-black mb-3 sm:mb-4" style={{ fontWeight: 600 }}>Get personalized support</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                    Our AI understands men's unique challenges. Get responses tailored to your situation, backed by proven mental health techniques and real empathy.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-auto">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">Evidence-based</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">Male-focused</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center group animate-fade-in-up opacity-0 animation-delay-1000 h-full">
              <div className="relative mb-6 h-full">
                <div className="relative bg-white border border-gray-200 rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm hover:shadow-lg transition-all duration-500 transform group-hover:scale-105 group-hover:-translate-y-2 h-full flex flex-col">
                  <div className="text-black font-bold text-lg mb-4 relative">
                    <span className="relative z-10 text-2xl" style={{ fontWeight: 700 }}>03</span>
                    <div className="absolute inset-0 bg-black rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 opacity-10"></div>
                  </div>
                 {/* <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-200 transition-colors duration-300">
                    <div className="space-y-1">
                      <div className="flex space-x-1">
                        <div className="w-1 h-2 bg-black"></div>
                        <div className="w-1 h-3 bg-black"></div>
                        <div className="w-1 h-4 bg-black"></div>
                        <div className="w-1 h-3 bg-black"></div>
                      </div>
                    </div>
                  </div> */}
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-black mb-3 sm:mb-4" style={{ fontWeight: 600 }}>Reflect on your journey</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                    Watch your growth unfold through your message history. Notice shifts in mood, spot recurring patterns, and celebrate meaningful progress—one conversation at a time.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-auto">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">Progress tracking</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-200">Insights & analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4 animate-fade-in-up opacity-0 animation-delay-300" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Trusted by Mental Health Professionals
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-400 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-8 h-8 border-2 border-black rounded-sm relative">
                  <div className="absolute inset-1 border border-black"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontWeight: 600 }}>Clinically Validated</h3>
              <p className="text-gray-600">Our approaches are backed by research and approved by licensed therapists.</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-600 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-6 h-8 border-2 border-black rounded-sm relative">
                  <div className="w-2 h-2 bg-black rounded-full absolute top-1 left-1"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontWeight: 600 }}>HIPAA Compliant</h3>
              <p className="text-gray-600">Your privacy is protected with bank-level security and encryption.</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-800 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-8 h-8 border-2 border-black rounded-full relative">
                  <div className="w-1 h-2 bg-black absolute top-1 left-3"></div>
                  <div className="w-2 h-1 bg-black absolute top-2 left-2.5"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontWeight: 600 }}>Expert Oversight</h3>
              <p className="text-gray-600">Developed with input from male mental health specialists and psychologists.</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-1000 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="space-y-1">
                  <div className="flex space-x-1 justify-center">
                    <div className="w-1 h-2 bg-black"></div>
                    <div className="w-1 h-3 bg-black"></div>
                    <div className="w-1 h-4 bg-black"></div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontWeight: 600 }}>Proven Results</h3>
              <p className="text-gray-600">87% of users report improved mood within the first two weeks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Men Need This Section */}
      <section id="breaking-silence" className="py-12 sm:py-16 lg:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 animate-fade-in-up opacity-0 animation-delay-300 px-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>Breaking the Silence</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto animate-fade-in-up opacity-0 animation-delay-500 px-4">
              Men face unique mental health challenges. It's time to address them head-on.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20">
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 sm:p-6 lg:p-8 text-center transition-all duration-500 hover:bg-gray-900/70 hover:scale-105 animate-fade-in-up opacity-0 animation-delay-600 group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>75%</div>
              <p className="text-gray-300 mb-4">of suicides are by men, yet they're 3x less likely to seek professional help</p>
              <div className="text-sm text-gray-400">— American Foundation for Suicide Prevention</div>
            </div>
            
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8 text-center transition-all duration-500 hover:bg-gray-900/70 hover:scale-105 animate-fade-in-up opacity-0 animation-delay-800 group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>1 in 5</div>
              <p className="text-gray-300 mb-4">men experience anxiety or depression, but many suffer in silence due to stigma</p>
              <div className="text-sm text-gray-400">— National Institute of Mental Health</div>
            </div>
            
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8 text-center transition-all duration-500 hover:bg-gray-900/70 hover:scale-105 animate-fade-in-up opacity-0 animation-delay-1000 group">
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 700 }}>49%</div>
              <p className="text-gray-300 mb-4">of men feel more depressed than they admit to family and friends</p>
              <div className="text-sm text-gray-400">— Priory Group Mental Health Study</div>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 mb-20">
            <div className="bg-gray-900/30 border border-gray-700 rounded-xl p-8 transition-all duration-500 hover:bg-gray-900/40 animate-fade-in-up opacity-0 animation-delay-1200">
              <h3 className="text-2xl font-bold text-gray-300 mb-6" style={{ fontWeight: 600 }}>The Problem</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500 mt-1">✗</span>
                  Traditional therapy feels intimidating
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500 mt-1">✗</span>
                  Long waiting times for appointments
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500 mt-1">✗</span>
                  Fear of being judged or misunderstood
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500 mt-1">✗</span>
                  Pressure to "man up" and handle things alone
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500 mt-1">✗</span>
                  Lack of male-focused mental health resources
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-900/30 border border-gray-700 rounded-xl p-8 transition-all duration-500 hover:bg-gray-900/40 animate-fade-in-up opacity-0 animation-delay-1400">
              <h3 className="text-2xl font-bold text-gray-200 mb-6" style={{ fontWeight: 600 }}>Our Solution</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400 mt-1">✓</span>
                  Anonymous, judgment-free conversations
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400 mt-1">✓</span>
                  Instant access whenever you need support
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400 mt-1">✓</span>
                  Understanding of male communication styles
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400 mt-1">✓</span>
                  Focus on practical coping strategies
                </li>
                <li className="flex items-start gap-3 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400 mt-1">✓</span>
                  Built specifically for men's mental health
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-gray-900/20 backdrop-blur-sm border border-gray-700 rounded-xl p-8 transition-all duration-500 hover:bg-gray-900/30 animate-fade-in-up opacity-0 animation-delay-1600">
              <blockquote className="text-lg md:text-xl text-gray-200 mb-6 leading-relaxed">
                "For years, I thought asking for help was a sign of weakness. Silas showed me that taking care of my mental health is actually the strongest thing I could do. The anonymity made it easier to open up."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center text-white font-semibold transform hover:scale-110 transition-transform duration-300">
                  MT
                </div>
                <div>
                  <div className="font-semibold text-white">Marcus T.</div>
                  <div className="text-gray-400">Software Engineer, 32</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-900/20 backdrop-blur-sm border border-gray-700 rounded-xl p-6 transition-all duration-500 hover:bg-gray-900/30 animate-fade-in-up opacity-0 animation-delay-1800">
                <blockquote className="text-gray-200 mb-4">
                  "Having someone available 24/7 changed everything. I don't have to wait for my breaking point anymore. It's like having a friend who actually gets what I'm going through."
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    JR
                  </div>
                  <div>
                    <div className="font-semibold text-white">James R.</div>
                    <div className="text-gray-400 text-sm">Construction Manager, 28</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-900/20 backdrop-blur-sm border border-gray-700 rounded-xl p-6 transition-all duration-500 hover:bg-gray-900/30 animate-fade-in-up opacity-0 animation-delay-2000">
                <blockquote className="text-gray-200 mb-4">
                  "The conversations feel real, not like I'm talking to a robot. It understands the pressure I face as a dad and husband without me having to explain everything."
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    DL
                  </div>
                  <div>
                    <div className="font-semibold text-white">David L.</div>
                    <div className="text-gray-400 text-sm">Father of 2, 41</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Preview Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up opacity-0 animation-delay-300">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
                Real Conversations, Real Support
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                See how Silas understands your struggles and provides practical, empathetic responses. 
                Every conversation is private, encrypted, and designed to help you feel heard and understood.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                    <div className="w-3 h-3 border border-black rounded-sm"></div>
                  </div>
                  <span className="text-gray-700">End-to-end encrypted conversations</span>
                </div>
                <div className="flex items-center gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                    <div className="w-2 h-2 bg-black"></div>
                  </div>
                  <span className="text-gray-700">Instant, intelligent responses</span>
                </div>
                <div className="flex items-center gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                    <div className="w-3 h-3 border border-black rounded-full"></div>
                  </div>
                  <span className="text-gray-700">Available 24/7, even late nights</span>
                </div>
                <div className="flex items-center gap-3 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                    <div className="w-1 h-3 bg-black"></div>
                  </div>
                  <span className="text-gray-700">Personalized to your situation</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105">
                  <div className="text-3xl font-bold text-black mb-2" style={{ fontWeight: 700 }}>&lt; 30s</div>
                  <div className="text-gray-600">Average response time</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105">
                  <div className="text-3xl font-bold text-black mb-2" style={{ fontWeight: 700 }}>94%</div>
                  <div className="text-gray-600">Find conversations helpful</div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center animate-fade-in-up opacity-0 animation-delay-500">
              <div className="relative max-w-sm w-full">
                <div className="bg-black rounded-3xl p-2 shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <div className="bg-white rounded-2xl overflow-hidden">
                    <div className="px-4 py-3 bg-gray-100 flex items-center gap-3 border-b border-gray-200">
                      <div className="relative">
                        <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white font-bold pulse-ring">
                          S
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-black font-medium">Silas</div>
                        <div className="flex items-center gap-1 text-green-600 text-sm">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          Online now
                        </div>
                      </div>
                      <button className="text-gray-400 text-xl hover:text-gray-600 transition-colors duration-300">⋯</button>
                    </div>
                    
                    <div className="p-4 space-y-4 h-96 overflow-y-auto bg-white">
                      <div className="flex gap-3 animate-slide-in-left">
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-sm font-bold">
                          S
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 text-black text-sm hover:bg-gray-200 transition-colors duration-300">
                            Hey there! I noticed you've been feeling overwhelmed lately. Want to talk about what's going on?
                          </div>
                          <div className="text-gray-500 text-xs mt-1">2:34 PM</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 justify-end animate-slide-in-right">
                        <div className="flex-1 max-w-xs">
                          <div className="bg-black rounded-2xl rounded-tr-md px-4 py-3 text-white text-sm hover:bg-gray-800 transition-colors duration-300">
                            Work stress is killing me. My boss keeps piling on more projects and I feel like I'm drowning
                          </div>
                          <div className="text-gray-500 text-xs mt-1 text-right">2:35 PM</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 animate-slide-in-left animation-delay-500">
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-sm font-bold">
                          S
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 text-black text-sm hover:bg-gray-200 transition-colors duration-300">
                            That sounds incredibly tough. Work pressure can feel suffocating, especially when it keeps building up. You're not alone in feeling this way.
                          </div>
                          <div className="text-gray-500 text-xs mt-1">2:35 PM</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 animate-slide-in-left animation-delay-1000">
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-sm font-bold">
                          S
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 text-black text-sm hover:bg-gray-200 transition-colors duration-300">
                            Have you been able to set any boundaries with your workload, or does it feel like everything is urgent?
                          </div>
                          <div className="text-gray-500 text-xs mt-1">2:36 PM</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 items-center animate-fade-in animation-delay-1500">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                        </div>
                        <span className="text-gray-500 text-sm">You're typing...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4 animate-fade-in-up opacity-0 animation-delay-300" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Everything You Need to Heal and Grow
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up opacity-0 animation-delay-500">
              Comprehensive tools designed specifically for men's mental health journey
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-black text-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-500 transform hover:scale-105 animate-fade-in-up opacity-0 animation-delay-600 group">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-700 transition-colors duration-300">
                <div className="w-8 h-8 border-2 border-white rounded-full relative">
                  <div className="w-2 h-2 bg-white rounded-full absolute top-1 left-1"></div>
                  <div className="w-2 h-2 bg-white rounded-full absolute top-1 right-1"></div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontWeight: 600 }}>AI-Powered Conversations</h3>
              <p className="text-gray-300 mb-6 text-lg">
                Advanced AI that understands male communication patterns and mental health needs.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400">✓</span>
                  Context-aware responses
                </div>
                <div className="flex items-center gap-2 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400">✓</span>
                  Learns your communication style
                </div>
                <div className="flex items-center gap-2 text-gray-300 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-400">✓</span>
                  Emotional intelligence built-in
                </div>
              </div>
            </div>
            
            {/* <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-800 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="space-y-1">
                  <div className="flex space-x-1 justify-center">
                    <div className="w-1 h-2 bg-black"></div>
                    <div className="w-1 h-3 bg-black"></div>
                    <div className="w-1 h-4 bg-black"></div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-4" style={{ fontWeight: 600 }}>Mood Tracking & Insights</h3>
              <p className="text-gray-600 mb-6">
                Understand your emotional patterns and identify triggers with smart analytics.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Daily mood check-ins
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Pattern recognition
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Progress visualization
                </div>
              </div>
            </div> */}
            
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-1000 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-8 h-8 border-2 border-black rounded-sm relative">
                  <div className="w-2 h-2 bg-black absolute top-1 left-1"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-4" style={{ fontWeight: 600 }}>Personalized Coping Strategies</h3>
              <p className="text-gray-600 mb-6">
                Receive tailored techniques based on your specific challenges and goals.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Evidence-based techniques
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Customized to your lifestyle
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Progressive skill building
                </div>
              </div>
            </div>
            
            {/* <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-1200 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-6 h-8 border-2 border-black rounded-sm relative">
                  <div className="w-2 h-2 bg-black rounded-full absolute top-1 left-1"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-4" style={{ fontWeight: 600 }}>Crisis Support</h3>
              <p className="text-gray-600 mb-6">
                Immediate help when you need it most, with emergency resources and escalation.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  24/7 crisis intervention
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Emergency contact system
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Professional referrals
                </div>
              </div>
            </div> */}
            
            {/* <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-1400 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="w-8 h-8 border-2 border-black rounded-sm relative">
                  <div className="w-4 h-1 bg-black absolute top-2 left-1"></div>
                  <div className="w-1 h-4 bg-black absolute top-1 left-3"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-4" style={{ fontWeight: 600 }}>Achievement Tracking</h3>
              <p className="text-gray-600 mb-6">
                Celebrate your progress with milestone tracking and achievement unlocks.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Goal setting & tracking
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Achievement badges
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Progress celebrations
                </div>
              </div>
            </div> */}
            
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up opacity-0 animation-delay-1600 group">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6 group-hover:bg-gray-300 transition-colors duration-300">
                <div className="flex space-x-1">
                  <div className="w-2 h-6 bg-black rounded-full"></div>
                  <div className="w-2 h-6 bg-black rounded-full"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-black mb-4" style={{ fontWeight: 600 }}>Anonymous Community</h3>
              <p className="text-gray-600 mb-6">
                Connect with other men facing similar challenges in a safe, moderated space.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Peer support groups
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Anonymous sharing
                </div>
                <div className="flex items-center gap-2 text-gray-600 transform hover:translate-x-2 transition-transform duration-300">
                  <span className="text-gray-500">✓</span>
                  Moderated discussions
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing-section" className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Free Trial Text */}
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black animate-fade-in-up opacity-0 animation-delay-300" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Try for free, Silas has a free trial
            </p>
          </div>
          {/* <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4 animate-fade-in-up opacity-0 animation-delay-300" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up opacity-0 animation-delay-500">
              Start with a free trial, then choose the plan that fits your needs.
            </p>
          </div> */}
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-8 sm:mb-12 animate-fade-in-up opacity-0 animation-delay-600">
            <div className="bg-white rounded-lg p-1 flex shadow-lg border border-gray-200">
              <button 
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-black text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                MONTHLY
              </button>
              <button 
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                  billingCycle === 'yearly'
                    ? 'bg-black text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                YEARLY (20% Off)
              </button>
            </div>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Starter Plan */}
            <div className="relative animate-fade-in-up opacity-0 animation-delay-700">
              <div className="h-full bg-black rounded-xl border border-gray-800 hover:border-gray-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Starter</h3>
                    <p className="text-gray-300 mb-6">Perfect for getting started</p>
                    <div className="mb-6">
                      <span className="text-2xl sm:text-3xl font-bold text-white">
                        ${billingCycle === 'yearly' ? '276' : '29'}
                      </span>
                      <span className="text-base ml-1 text-gray-400">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600 mb-3">
                      <p className="text-sm font-semibold text-gray-200">750 messages included</p>
                    </div>
                    {billingCycle === 'yearly' && (
                      <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600">
                        <p className="text-sm font-semibold text-gray-200">$23 per month</p>
                      </div>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Unlimited AI conversations</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Basic mood tracking</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Priority support</span>
                    </li>
                  </ul>
                  
                  <button 
                    onClick={handleSignUp}
                    className="w-full py-3 sm:py-4 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium bg-white text-black hover:bg-gray-100 transition-all duration-300"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </div>
            
            {/* Standard Plan */}
            <div className="relative animate-fade-in-up opacity-0 animation-delay-800">
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-black text-white text-sm px-3 py-1 rounded-full shadow-lg font-medium border border-white">
                  POPULAR
                </div>
              </div>
              <div className="h-full bg-black rounded-xl border border-gray-800 hover:border-gray-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Standard</h3>
                    <p className="text-gray-300 mb-6">Most comprehensive support</p>
                    <div className="mb-6">
                      <span className="text-2xl sm:text-3xl font-bold text-white">
                        ${billingCycle === 'yearly' ? '468' : '49'}
                      </span>
                      <span className="text-base ml-1 text-gray-400">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600 mb-3">
                      <p className="text-sm font-semibold text-gray-200">1,750 messages included</p>
                    </div>
                    {billingCycle === 'yearly' && (
                      <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600">
                        <p className="text-sm font-semibold text-gray-200">$39 per month</p>
                      </div>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Everything in Starter</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Advanced analytics & insights</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-200 text-sm leading-relaxed">Goal setting & tracking</span>
                    </li>
                  </ul>
                  
                  <button 
                    onClick={handleSignUp}
                    className="w-full py-3 sm:py-4 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium bg-white text-black hover:bg-gray-100 transition-all duration-300"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </div>
            
            {/* Pro Plan */}
            <div className="relative animate-fade-in-up opacity-0 animation-delay-900">
              <div className="h-full bg-black rounded-xl border border-gray-800 hover:border-gray-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Pro</h3>
                    <p className="text-gray-300 mb-6">Access to AI assistant with unlimited conversations</p>
                    <div className="mb-6">
                      <span className="text-2xl sm:text-3xl font-bold text-white">
                        ${billingCycle === 'yearly' ? '948' : '99'}
                      </span>
                      <span className="text-base ml-1 text-gray-400">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600 mb-3">
                      <p className="text-sm font-semibold text-gray-200">4,000 messages included</p>
                    </div>
                    {billingCycle === 'yearly' && (
                      <div className="py-3 px-4 rounded-lg border bg-gray-800 border-gray-600">
                        <p className="text-sm font-semibold text-gray-200">$79 per month</p>
                      </div>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Everything in Standard</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Direct therapist consultations</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">Personalized treatment plans</span>
                    </li>
                  </ul>
                  
                  <button 
                    onClick={handleSignUp}
                    className="w-full py-3 sm:py-4 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium bg-white text-black hover:bg-gray-100 transition-all duration-300"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 animate-fade-in-up opacity-0 animation-delay-300 px-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
            Your Mental Health Journey Starts Now
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-10 leading-relaxed animate-fade-in-up opacity-0 animation-delay-500 px-4">
          Thousands of men have already taken the first step toward better mental health with Silas. Start your free conversation today—no pressure, no judgment, just support that meets you where you are.
          </p>
          
          <div className="mb-8 animate-fade-in-up opacity-0 animation-delay-700">
            <button 
              onClick={isSignedIn ? () => navigate('/home') : handleSignUp}
              className="px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-lg sm:rounded-xl font-bold text-base sm:text-lg lg:text-xl transition-all duration-300 shadow-sm hover:shadow-lg transform hover:scale-105 inline-flex items-center gap-2 sm:gap-3 bg-white hover:bg-gray-100 text-black group"
            >
              {isSignedIn ? (
                <>
                  Go to Dashboard
                  <span className="text-lg sm:text-xl lg:text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                </>
              ) : (
                <>
                  Start Your First Conversation
                  <span className="text-lg sm:text-xl lg:text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                </>
              )}
            </button>
          </div>
          
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 sm:gap-6 text-gray-300 animate-fade-in-up opacity-0 animation-delay-900 text-sm sm:text-base">
            <div className="flex items-center gap-2 transform hover:scale-105 transition-transform duration-300">
              <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                <div className="w-2 h-2 border border-gray-400 rounded-sm"></div>
              </div>
              100% Private & Encrypted
            </div>
            <div className="flex items-center gap-2 transform hover:scale-105 transition-transform duration-300">
              <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400"></div>
              </div>
              Instant Access
            </div>
            <div className="flex items-center gap-2 transform hover:scale-105 transition-transform duration-300">
              <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
              Always Free to Start
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
            <div className="lg:col-span-2 animate-fade-in-up opacity-0 animation-delay-300">
              <div className="mb-6">
                <img 
                  src="/images/noBgColor.png" 
                  alt="Silas Logo" 
                  className="h-8 w-auto object-contain transition-transform duration-300 hover:scale-105"
                  style={{
                    filter: 'invert(1) brightness(1.2)',
                    transition: 'filter 0.3s ease'
                  }}
                />
              </div>
              <p className="text-gray-300 mb-6 text-lg">
                Mental health support that fits your life.
              </p>
              
              <div className="mb-8">
                <h4 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4" style={{ fontWeight: 600 }}>Our Mission</h4>
                <p className="text-gray-300 leading-relaxed">
                  Breaking down barriers to mental health care for men. Every conversation matters, 
                  every step forward counts, and every man deserves support without judgment.
                </p>
              </div>
            </div>
            
            <div className="animate-fade-in-up opacity-0 animation-delay-500">
              <div className="grid grid-cols-1 gap-8 mb-8">
                <div>
                  <h5 className="font-semibold text-white mb-6" style={{ fontWeight: 600 }}>Quick Links</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                    <button 
                      onClick={() => navigate('/about')}
                      className="block text-gray-300 hover:text-white transition-all duration-300 transform hover:translate-x-1 text-left"
                    >
                      About Us
                    </button>
                    <button 
                      onClick={handleContactUs}
                      className="block text-gray-300 hover:text-white transition-all duration-300 transform hover:translate-x-1 text-left"
                    >
                      Contact Us
                    </button>
                    <button 
                      onClick={() => navigate('/privacy')}
                      className="block text-gray-300 hover:text-white transition-all duration-300 transform hover:translate-x-1 text-left"
                    >
                      Privacy Policy
                    </button>
                    <button 
                      onClick={() => navigate('/terms')}
                      className="block text-gray-300 hover:text-white transition-all duration-300 transform hover:translate-x-1 text-left"
                    >
                      Terms of Service
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4">
                <a href="#" className="text-gray-300 hover:text-white transition-all duration-300 transform hover:scale-110">Twitter</a>
                <a href="https://www.instagram.com/silashealth?igsh=MTBpYnk1dTJtZ2p4Mg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-300 transform hover:scale-110">Instagram</a>
                <a href="#" className="text-gray-300 hover:text-white transition-all duration-300 transform hover:scale-110">LinkedIn</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 animate-fade-in-up opacity-0 animation-delay-700">
            <p className="text-gray-400">
              &copy; 2025 Silas. Your mental health matters.
            </p>
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-full px-4 py-2 transition-all duration-300 hover:bg-gray-700 transform hover:scale-105">
              <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                <div className="w-3 h-3 border border-gray-300 rounded-sm"></div>
              </div>
              <span className="text-gray-300 text-sm font-medium">HIPAA Compliant & Secure</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {isContactFormOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseContactForm}
        >
          <div 
            className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-black">Contact Us</h3>
              <button 
                onClick={handleCloseContactForm}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <form ref={formRef} onSubmit={handleContactFormSubmit} className="space-y-4">
              {/* Hidden field for recipient email */}
              <input type="hidden" name="to_email" value="wajahat.test@gmail.com" />
              {/* Error Messages */}
              {formErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-sm text-red-600">
                    {formErrors.map((error, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-red-500">•</span>
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success/Error Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    {submitMessage}
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <div className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✕</span>
                    </div>
                    {submitMessage}
                  </div>
                </div>
              )}
              
              <div>
                <label htmlFor="from_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="from_name"
                  name="from_name"
                  value={contactForm.name}
                  onChange={handleContactFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="from_email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="from_email"
                  name="from_email"
                  value={contactForm.email}
                  onChange={handleContactFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                  placeholder="your.email@example.com"
                />
                {/* Hidden field for reply_to */}
                <input type="hidden" name="reply_to" value={contactForm.email} />
              </div>
              
              <div>
                <label htmlFor="user_subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="user_subject"
                  name="user_subject"
                  value={contactForm.subject}
                  onChange={handleContactFormChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                  placeholder="How can we help you?"
                />
                {/* Hidden field for full subject */}
                <input type="hidden" name="subject" value={`Contact Form: ${contactForm.subject}`} />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactFormChange}
                  rows={4}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200"
                  placeholder="Tell us about your question or concern..."
                ></textarea>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded-sm flex items-center justify-center">
                    <div className="w-2 h-1.5 bg-white rounded-sm"></div>
                  </div>
                  Your message will be sent directly to our support team at wajahat.test@gmail.com
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className={`flex-1 py-3 px-4 rounded-md transition-all duration-300 font-medium transform hover:scale-105 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${
                    isSubmitting 
                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                      : submitStatus === 'success'
                      ? 'bg-green-600 text-white cursor-not-allowed'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : submitStatus === 'success' ? (
                    <>
                      <span className="text-white">✓</span>
                      Sent Successfully
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseContactForm}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-md hover:bg-gray-300 transition-all duration-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Floating emoji animations */
        @keyframes float-1 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-15px) rotate(5deg) scale(1.1); }
          50% { transform: translateY(-25px) rotate(-3deg) scale(1.05); }
          75% { transform: translateY(-10px) rotate(2deg) scale(1.08); }
        }

        @keyframes float-2 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-20px) rotate(-5deg) scale(1.15); }
          66% { transform: translateY(-30px) rotate(4deg) scale(1.1); }
        }

        @keyframes float-3 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          20% { transform: translateY(-18px) rotate(3deg) scale(1.12); }
          40% { transform: translateY(-35px) rotate(-2deg) scale(1.08); }
          60% { transform: translateY(-22px) rotate(4deg) scale(1.15); }
          80% { transform: translateY(-12px) rotate(-1deg) scale(1.05); }
        }

        @keyframes float-4 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-28px) rotate(6deg) scale(1.2); }
        }

        @keyframes float-5 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-22px) rotate(-4deg) scale(1.1); }
          75% { transform: translateY(-15px) rotate(3deg) scale(1.08); }
        }

        @keyframes float-6 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          30% { transform: translateY(-25px) rotate(2deg) scale(1.18); }
          70% { transform: translateY(-18px) rotate(-3deg) scale(1.12); }
        }

        @keyframes float-7 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-32px) rotate(-5deg) scale(1.14); }
          80% { transform: translateY(-20px) rotate(4deg) scale(1.09); }
        }

        @keyframes float-8 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          35% { transform: translateY(-27px) rotate(7deg) scale(1.16); }
          65% { transform: translateY(-19px) rotate(-2deg) scale(1.11); }
        }

        @keyframes float-9 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          45% { transform: translateY(-24px) rotate(-6deg) scale(1.13); }
        }

        @keyframes float-10 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-21px) rotate(4deg) scale(1.1); }
          66% { transform: translateY(-16px) rotate(-3deg) scale(1.07); }
        }

        @keyframes float-11 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-29px) rotate(-4deg) scale(1.17); }
        }

        @keyframes float-12 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-17px) rotate(5deg) scale(1.09); }
          75% { transform: translateY(-26px) rotate(-2deg) scale(1.14); }
        }

        @keyframes float-13 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-31px) rotate(3deg) scale(1.19); }
        }

        @keyframes float-14 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          30% { transform: translateY(-23px) rotate(-5deg) scale(1.12); }
          70% { transform: translateY(-14px) rotate(2deg) scale(1.06); }
        }

        @keyframes float-15 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-33px) rotate(6deg) scale(1.21); }
        }

        @keyframes float-16 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-19px) rotate(-3deg) scale(1.08); }
          75% { transform: translateY(-13px) rotate(4deg) scale(1.11); }
        }

        @keyframes float-17 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-26px) rotate(5deg) scale(1.15); }
          66% { transform: translateY(-20px) rotate(-4deg) scale(1.1); }
        }

        @keyframes float-18 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          45% { transform: translateY(-28px) rotate(-2deg) scale(1.13); }
        }

        @keyframes float-19 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          35% { transform: translateY(-24px) rotate(7deg) scale(1.18); }
          70% { transform: translateY(-16px) rotate(-1deg) scale(1.09); }
        }

        @keyframes float-20 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-30px) rotate(-6deg) scale(1.16); }
        }

        @keyframes float-21 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-22px) rotate(3deg) scale(1.12); }
          80% { transform: translateY(-17px) rotate(-5deg) scale(1.07); }
        }

        @keyframes float-22 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          30% { transform: translateY(-25px) rotate(-4deg) scale(1.14); }
        }

        @keyframes float-23 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-34px) rotate(8deg) scale(1.22); }
        }

        @keyframes float-24 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-21px) rotate(-2deg) scale(1.1); }
          75% { transform: translateY(-18px) rotate(5deg) scale(1.13); }
        }

        @keyframes float-25 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-27px) rotate(-7deg) scale(1.17); }
        }

        @keyframes float-26 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-20px) rotate(4deg) scale(1.11); }
          66% { transform: translateY(-15px) rotate(-3deg) scale(1.08); }
        }

        @keyframes float-27 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          35% { transform: translateY(-29px) rotate(6deg) scale(1.19); }
          70% { transform: translateY(-23px) rotate(-1deg) scale(1.14); }
        }

        @keyframes float-28 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          45% { transform: translateY(-26px) rotate(-5deg) scale(1.15); }
        }

        @keyframes float-29 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-31px) rotate(4deg) scale(1.2); }
        }

        @keyframes float-30 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          30% { transform: translateY(-24px) rotate(-6deg) scale(1.13); }
          70% { transform: translateY(-19px) rotate(2deg) scale(1.1); }
        }

        @keyframes float-31 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-28px) rotate(5deg) scale(1.16); }
        }

        @keyframes float-32 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          25% { transform: translateY(-22px) rotate(-3deg) scale(1.12); }
          75% { transform: translateY(-17px) rotate(7deg) scale(1.09); }
        }

        @keyframes float-33 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-32px) rotate(-4deg) scale(1.18); }
        }

        @keyframes float-34 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-25px) rotate(6deg) scale(1.14); }
          66% { transform: translateY(-20px) rotate(-2deg) scale(1.11); }
        }

        @keyframes float-35 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          40% { transform: translateY(-30px) rotate(-8deg) scale(1.17); }
        }

        @keyframes float-36 {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          35% { transform: translateY(-27px) rotate(3deg) scale(1.21); }
          65% { transform: translateY(-21px) rotate(-5deg) scale(1.15); }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slide-in-left 0.6s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.6s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-500 {
          animation-delay: 0.5s;
        }

        .animation-delay-700 {
          animation-delay: 0.7s;
        }

        .animation-delay-900 {
          animation-delay: 0.9s;
        }

        .animation-delay-1100 {
          animation-delay: 1.1s;
        }

        .animation-delay-1300 {
          animation-delay: 1.3s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animation-delay-800 {
          animation-delay: 0.8s;
        }

        .animation-delay-1000 {
          animation-delay: 1.0s;
        }

        .animation-delay-1200 {
          animation-delay: 1.2s;
        }

        .animation-delay-1400 {
          animation-delay: 1.4s;
        }

        .animation-delay-1500 {
          animation-delay: 1.5s;
        }

        .animation-delay-1600 {
          animation-delay: 1.6s;
        }

        .animation-delay-1800 {
          animation-delay: 1.8s;
        }

        .animation-delay-2000 {
          animation-delay: 2.0s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .pulse-ring {
          position: relative;
        }

        .pulse-ring::before {
          content: '';
          position: absolute;
          inset: -4px;
          border: 2px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          animation: pulse-ring 2s ease-out infinite;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
